import { createContext, useCallback, useContext, useState } from 'react';

type QuestionEntity = SurveyQuestionMultipleChoiceEntity;
type ChoiceEntity = SurveyQuestionChoiceEntity;

export interface SurveyParticipateContextType {
  answers: SurveyAnswer[],
  addChoice: (question: QuestionEntity, choice: ChoiceEntity) => void;
  removeChoice: (question: QuestionEntity, choice: ChoiceEntity) => void;
  removeChoiceById: (question: ChoiceEntity, choiceId: number) => void;
}

const SurveyParticipateContext = createContext<SurveyParticipateContextType>({
  answers: [],
  addChoice: null,
  removeChoice: null,
  removeChoiceById: null
});

export const useSurveyParticipateContext = () => useContext(SurveyParticipateContext);

const SurveyParticipateContextProvider = ({ children }) => {

  const [answers, setAnswers] = useState<SurveyAnswer[]>([]);

  const addChoice = useCallback((question: ChoiceEntity, choice: QuestionEntity) => {
    setAnswers(answers => {
      const newAnswers = [...answers];
      const index = answers.findIndex(a => a.questionId === question.id);
      if (index >= 0) {
        newAnswers[index].answerIds = [...(newAnswers[index]?.answerIds ?? []), choice.id];
        return newAnswers;
      }
      newAnswers.push({ questionId: question.id, answerIds: [choice.id] });
      return newAnswers;
    });
  }, [setAnswers]);

  const removeChoiceById = useCallback((question: ChoiceEntity, choiceId: number) => {
    setAnswers(answers => {
      const newAnswers = [...answers];
      const questionIndex = answers.findIndex(a =>
        a.questionId === question.id &&
        a.answerIds?.includes(choiceId)
      );
      if (questionIndex >= 0) {
        const choiceIndex = newAnswers[questionIndex].answerIds?.findIndex(id => id === choiceId);
        if (choiceIndex >= 0) {
          newAnswers[questionIndex].answerIds.splice(choiceIndex, 1);
          if (newAnswers[questionIndex].answerIds.length === 0) {
            newAnswers.splice(questionIndex, 1);
          }
        }
        return newAnswers;
      }
      return newAnswers;
    });
  }, [setAnswers]);

  const removeChoice = useCallback((question: ChoiceEntity, choice: QuestionEntity) => {
    removeChoiceById(question, choice.id);
  }, [removeChoiceById]);

  return (
    <SurveyParticipateContext.Provider value={{
      answers,
      addChoice,
      removeChoice,
      removeChoiceById
    }}>
      {children}
    </SurveyParticipateContext.Provider>
  );
};

export default SurveyParticipateContextProvider;
